"use client";

import Image from "next/image";
import type { CSSProperties } from "react";
import { mediaUrl } from "@/lib/utils";

export interface SlideMediaFields {
  id: string;
  title: string;
  mediaType?: "image" | "video" | "animatedImage";
  mediaUrl?: string;
  mobileMediaUrl?: string;
  posterImageUrl?: string;
  imageUrl: string;
  mobileImageUrl?: string;
  focalX?: number;
  focalY?: number;
  mobileFocalX?: number;
  mobileFocalY?: number;
}

interface SlideMediaProps {
  slide: SlideMediaFields;
  alt?: string;
  priority?: boolean;
  sizes: string;
  className?: string;
  mobileClassName?: string;
  desktopClassName?: string;
  style?: CSSProperties;
  mobileStyle?: CSSProperties;
}

export function getSlideMediaUrl(slide: Pick<SlideMediaFields, "mediaUrl" | "imageUrl">) {
  return slide.mediaUrl || slide.imageUrl;
}

export function hasSlideMedia(slide: Pick<SlideMediaFields, "mediaUrl" | "imageUrl">) {
  return Boolean(getSlideMediaUrl(slide));
}

export function SlideMedia({
  slide,
  alt = slide.title,
  priority = false,
  sizes,
  className = "object-cover",
  mobileClassName,
  desktopClassName,
  style,
  mobileStyle,
}: SlideMediaProps) {
  const mediaType = slide.mediaType ?? "image";
  const desktopUrl = getSlideMediaUrl(slide);
  const mobileUrl = slide.mobileMediaUrl || slide.mobileImageUrl;
  const resolvedDesktop = mediaUrl(desktopUrl);
  const resolvedMobile = mobileUrl ? mediaUrl(mobileUrl) : null;
  const desktopStyle = {
    objectPosition: `${slide.focalX ?? 50}% ${slide.focalY ?? 50}%`,
    ...style,
  };
  const resolvedMobileStyle = {
    objectPosition: `${slide.mobileFocalX ?? 50}% ${slide.mobileFocalY ?? 50}%`,
    ...mobileStyle,
  };
  const desktopClass = desktopClassName ?? `${resolvedMobile ? "hidden sm:block" : "block"} ${className}`;
  const mobileClass = mobileClassName ?? `block ${className} sm:hidden`;

  return (
    <>
      {resolvedMobile && renderMedia({
        mediaType,
        src: resolvedMobile,
        alt,
        priority,
        sizes,
        posterImageUrl: slide.posterImageUrl,
        className: mobileClass,
        style: resolvedMobileStyle,
      })}
      {renderMedia({
        mediaType,
        src: resolvedDesktop,
        alt,
        priority,
        sizes,
        posterImageUrl: slide.posterImageUrl,
        className: desktopClass,
        style: desktopStyle,
      })}
    </>
  );
}

function renderMedia({
  mediaType,
  src,
  alt,
  priority,
  sizes,
  posterImageUrl,
  className,
  style,
}: {
  mediaType: string;
  src: string;
  alt: string;
  priority: boolean;
  sizes: string;
  posterImageUrl?: string;
  className: string;
  style: CSSProperties;
}) {
  if (mediaType === "video") {
    return (
      <video
        src={src}
        poster={posterImageUrl ? mediaUrl(posterImageUrl) : undefined}
        autoPlay
        muted
        loop
        playsInline
        className={`absolute inset-0 h-full w-full ${className}`}
        style={style}
      />
    );
  }

  if (mediaType === "animatedImage") {
    return (
      <Image
        src={src}
        alt={alt}
        fill
        unoptimized
        priority={priority}
        sizes={sizes}
        className={className}
        style={style}
      />
    );
  }

  return (
    <Image
      src={src}
      alt={alt}
      fill
      priority={priority}
      sizes={sizes}
      style={style}
      className={className}
    />
  );
}
